<template>
  <section id="skills" class="py-20 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <h2 class="section-title mb-4" :class="{ 'khmer': isKhmer }">
          {{ $t('skills.title') }}
        </h2>
        <p class="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          A comprehensive overview of my technical expertise and the tools I use to bring ideas to life
        </p>
      </div>

      <!-- Skills & Technologies Section -->
      <div class="mb-20">
        <div class="text-center mb-12">
          <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Skills & Technologies
          </h3>
          <div class="w-24 h-1 bg-primary-500 mx-auto rounded-full"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Frontend Skills -->
          <div class="group">
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
              <div class="flex items-center mb-8">
                <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                  <span class="text-3xl">🎨</span>
                </div>
                <div>
                  <h4 class="text-2xl font-bold text-gray-900 dark:text-white" :class="{ 'khmer': isKhmer }">
                    {{ $t('skills.frontend') }}
                  </h4>
                  <p class="text-gray-600 dark:text-gray-400">User Interface & Experience</p>
                </div>
              </div>
              <div class="space-y-6">
                <SkillItem
                  v-for="skill in frontendSkills"
                  :key="skill.name"
                  :name="skill.name"
                  :level="skill.level"
                  :icon="skill.icon"
                  :image="skill.image"
                  :color="skill.color"
                />
              </div>
            </div>
          </div>

          <!-- Backend Skills -->
          <div class="group">
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
              <div class="flex items-center mb-8">
                <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                  <span class="text-3xl">⚙️</span>
                </div>
                <div>
                  <h4 class="text-2xl font-bold text-gray-900 dark:text-white" :class="{ 'khmer': isKhmer }">
                    {{ $t('skills.backend') }}
                  </h4>
                  <p class="text-gray-600 dark:text-gray-400">Server & Logic</p>
                </div>
              </div>
              <div class="space-y-6">
                <SkillItem
                  v-for="skill in backendSkills"
                  :key="skill.name"
                  :name="skill.name"
                  :level="skill.level"
                  :icon="skill.icon"
                  :image="skill.image"
                  :color="skill.color"
                />
              </div>
            </div>
          </div>

          <!-- Database Skills -->
          <div class="group">
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
              <div class="flex items-center mb-8">
                <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                  <span class="text-3xl">🗄️</span>
                </div>
                <div>
                  <h4 class="text-2xl font-bold text-gray-900 dark:text-white" :class="{ 'khmer': isKhmer }">
                    {{ $t('skills.databases') }}
                  </h4>
                  <p class="text-gray-600 dark:text-gray-400">Data Management</p>
                </div>
              </div>
              <div class="space-y-6">
                <SkillItem
                  v-for="skill in databaseSkills"
                  :key="skill.name"
                  :name="skill.name"
                  :level="skill.level"
                  :icon="skill.icon"
                  :image="skill.image"
                  :color="skill.color"
                />
              </div>
            </div>
          </div>

          <!-- DevOps & Tools -->
          <div class="group">
            <div class="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700">
              <div class="flex items-center mb-8">
                <div class="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                  <span class="text-3xl">🚀</span>
                </div>
                <div>
                  <h4 class="text-2xl font-bold text-gray-900 dark:text-white" :class="{ 'khmer': isKhmer }">
                    {{ $t('skills.devops') }}
                  </h4>
                  <p class="text-gray-600 dark:text-gray-400">Development & Operations</p>
                </div>
              </div>
              <div class="space-y-6">
                <SkillItem
                  v-for="skill in devopsSkills"
                  :key="skill.name"
                  :name="skill.name"
                  :level="skill.level"
                  :icon="skill.icon"
                  :image="skill.image"
                  :color="skill.color"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Technologies I Work With Section -->
      <div class="relative">
        <div class="text-center mb-12">
          <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Technologies I Work With
          </h3>
          <div class="w-24 h-1 bg-primary-500 mx-auto rounded-full mb-4"></div>
          <p class="text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
            The tools and platforms that power my development workflow
          </p>
        </div>

        <!-- Tech Stack Grid -->
        <div class="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-6">
            <div
              v-for="tech in techStack"
              :key="tech.name"
              class="group relative flex flex-col items-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-2xl hover:from-primary-50 hover:to-primary-100 dark:hover:from-primary-900/20 dark:hover:to-primary-800/20 transition-all duration-300 hover:scale-110 hover:shadow-lg cursor-pointer"
            >
              <!-- Tech Icon -->
              <div class="text-4xl mb-3 group-hover:scale-125 transition-transform duration-300">
                {{ tech.icon }}
              </div>
              
              <!-- Tech Name -->
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400 text-center transition-colors duration-200">
                {{ tech.name }}
              </span>
              
              <!-- Hover Effect Overlay -->
              <div class="absolute inset-0 bg-gradient-to-br from-primary-500/0 to-primary-600/0 group-hover:from-primary-500/5 group-hover:to-primary-600/5 rounded-2xl transition-all duration-300"></div>
            </div>
          </div>
        </div>

        <!-- Floating Elements for Visual Interest -->
        <div class="absolute -top-4 -left-4 w-24 h-24 bg-primary-200 dark:bg-primary-800 rounded-full opacity-20 blur-xl"></div>
        <div class="absolute -bottom-8 -right-8 w-32 h-32 bg-purple-200 dark:bg-purple-800 rounded-full opacity-20 blur-xl"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import SkillItem from './SkillItem.vue'

// Import icons
import vueIcon from '@/assets/icons/vue.png'
import reactIcon from '@/assets/icons/react.png'
import typescriptIcon from '@/assets/icons/typescript.png'
import tailwindIcon from '@/assets/icons/tailwind.png'
import javascriptIcon from '@/assets/icons/javascript.png'
import nodejsIcon from '@/assets/icons/nodejs.png'
import expressIcon from '@/assets/icons/express.png'
import pythonIcon from '@/assets/icons/python.png'
import phpIcon from '@/assets/icons/php.png'
import firebaseIcon from '@/assets/icons/firebase.png'
import mysqlIcon from '@/assets/icons/mysql.png'
import postgresqlIcon from '@/assets/icons/postgresql.png'
import redisIcon from '@/assets/icons/redis.png'
import dockerIcon from '@/assets/icons/docker.png'
import gitIcon from '@/assets/icons/git.png'
import linuxIcon from '@/assets/icons/linux.png'
import laravelIcon from '@/assets/icons/laravel.png'

const store = useStore()
const isKhmer = computed(() => store.getters.isKhmer)

const frontendSkills = [
  { name: 'Vue.js', level: 95, icon: '🟢', image: vueIcon, color: 'green' },
  { name: 'React', level: 90, icon: '⚛️', image: reactIcon, color: 'blue' },
  { name: 'Laravel', level: 85, icon: '📘', image: laravelIcon, color: 'blue' },
  { name: 'TypeScript', level: 85, icon: '📘', image: typescriptIcon, color: 'blue' },
  { name: 'Tailwind CSS', level: 90, icon: '🎨', image: tailwindIcon, color: 'cyan' },
  { name: 'JavaScript', level: 95, icon: '⚡', image: javascriptIcon, color: 'yellow' }
]

const backendSkills = [
  { name: 'Node.js', level: 90, icon: '🟢', image: nodejsIcon, color: 'green' },
  { name: 'Express.js', level: 85, icon: '🚀', image: expressIcon, color: 'gray' },
  { name: 'Python', level: 80, icon: '🐍', image: pythonIcon, color: 'blue' },
  { name: 'PHP', level: 75, icon: '🐘', image: phpIcon, color: 'purple' },
  { name: 'REST APIs', level: 90, icon: '🔗', color: 'orange' }
]

const databaseSkills = [
  { name: 'Firebase', level: 85, icon: '🍃', image: firebaseIcon, color: 'green' },
  { name: 'MySQL', level: 80, icon: '🐬', image: mysqlIcon, color: 'blue' },
  { name: 'PostgreSQL', level: 75, icon: '🐘', image: postgresqlIcon, color: 'blue' },
  { name: 'Redis', level: 70, icon: '🔴', image: redisIcon, color: 'red' }
]

const devopsSkills = [
  { name: 'Docker', level: 80, icon: '🐳', image: dockerIcon, color: 'blue' },
  { name: 'Git', level: 95, icon: '📝', image: gitIcon, color: 'orange' },
  { name: 'Linux', level: 85, icon: '🐧', image: linuxIcon, color: 'black' }
]

const techStack = [
  { name: 'Vue.js', icon: '💚' },
  { name: 'Node.js', icon: '🟢' },
  { name: 'TypeScript', icon: '📘' },
  { name: 'Docker', icon: '🐳' },
  { name: 'Git', icon: '📝' },
  { name: 'Tailwind', icon: '🎨' },
  { name: 'Vercel', icon: '⚡' },
  { name: 'Figma', icon: '🎭' },
  { name: 'VS Code', icon: '💻' }
]
</script> 